import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createWarehouseLocation } from '#/api/system/warehouseLocation/create';
import { fetchWarehouseLocationDetail } from '#/api/system/warehouseLocation/fetchDetail';
import { updateWarehouseLocation } from '#/api/system/warehouseLocation/update';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { warehouseLocationFormSchema } from '#/modules/imsMgmt/settings/storePlace/schemas/warehouseLocationSchema';
import {
  getWarehouseLocationApiErrorMessage,
  isWarehouseLocationVersionConflict,
} from '#/modules/imsMgmt/settings/storePlace/helper/apiError';
import { WAREHOUSE_LOCATION_FIELD_LABELS } from '#/modules/imsMgmt/settings/storePlace/helper/formValidationLabels';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  warehouseLocationDetailQueryKey,
  warehouseLocationListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditWarehouseLocationPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const updatedAtRef = useRef(null);
  const presetWarehouseId = searchParams.get('warehouseId') || '';
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const warehouseOptionsQuery = useQuery({
    queryKey: ['erp', 'warehouse', 'selectOptions'],
    queryFn: fetchWarehouseSelectOptions,
  });

  const detailQuery = useQuery({
    queryKey: warehouseLocationDetailQueryKey(id),
    queryFn: () => fetchWarehouseLocationDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createWarehouseLocation,
    updateFn: updateWarehouseLocation,
    buildPayload: (values) => {
      const payload = {
        warehouseId: values.warehouseId,
        code: values.code,
        name: values.name,
        remark: values.remark || null,
      };

      if (id) {
        return {
          ...payload,
          updatedAt: updatedAtRef.current,
        };
      }

      return payload;
    },
    listQueryKeyPrefix: warehouseLocationListQueryKeyPrefix,
    detailQueryKey: warehouseLocationDetailQueryKey,
    getEditPath: ROUTES.storePlaceLocationEdit,
    getErrorMessage: (error) => getWarehouseLocationApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isWarehouseLocationVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(warehouseLocationFormSchema),
    defaultValues: {
      warehouseId: presetWarehouseId,
      code: '',
      name: '',
      remark: '',
    },
  });

  useEffect(() => {
    if (isEdit) {
      return;
    }

    if (presetWarehouseId) {
      setValue('warehouseId', presetWarehouseId, { shouldValidate: true });
    }
  }, [isEdit, presetWarehouseId, setValue]);

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset({
      warehouseId: detail.warehouseId ?? detail.warehouse?.id ?? '',
      code: detail.code ?? '',
      name: detail.name ?? '',
      remark: detail.remark ?? '',
    });
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯儲位' : '新增儲位';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const warehouseOptions = warehouseOptionsQuery.data ?? [];

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-lg">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.storePlaceLocation}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getWarehouseLocationApiErrorMessage(detailQuery.error, '找不到儲位資料')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={WAREHOUSE_LOCATION_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body">
                  <FloatingSelect
                    id="warehouseId"
                    label="倉別"
                    options={warehouseOptions}
                    control={control}
                    errors={errors}
                    isDisabled={warehouseOptionsQuery.isLoading}
                    noOptionsMessage="無可用倉別"
                  />
                  <FloatingInput
                    id="code"
                    label="代號"
                    register={register}
                    errors={errors}
                  />
                  <FloatingInput
                    id="name"
                    label="名稱"
                    register={register}
                    errors={errors}
                  />
                  <FloatingTextarea
                    id="remark"
                    label="備註"
                    register={register}
                    errors={errors}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.storePlaceLocation}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditWarehouseLocationPage;
